/**
 * Certificate of Vaccination — interactive form for populating vaccine
 * details before generating the PDF.
 */
import { Input } from '@/frontend/components/ui/input'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import { OwnerDetailsPreview } from '@/frontend/features/printing/documents/blocks/owner-details'
import { PatientDetailsPreview } from '@/frontend/features/printing/documents/blocks/patient-details'
import { VetSignoffPreview } from '@/frontend/features/printing/documents/blocks/vet-signoff'
import type { DocumentFormProps } from '@/frontend/features/printing/documents/types'
import {
  defaultVaccinationCertificateFields,
  type VaccinationCertificateFields,
  VaccinationCertificatePdf,
} from '@/frontend/features/printing/documents/vaccination-certificate.print'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

function VaccinationCertificateForm({
  context,
  value,
  onChange,
}: DocumentFormProps<VaccinationCertificateFields>) {
  const { patient, clinic } = context

  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} />

      <h1 className="mb-4 text-center text-lg">Certificate of Vaccination</h1>

      <OwnerDetailsPreview owner={context.owner} />
      <div className="mt-3">
        <PatientDetailsPreview patient={patient} weight={context.weight} showDob showMicrochip />
      </div>

      <p className="mt-4">This is a document to certify {patient.name}'s vaccination details.</p>

      {/* Vaccine details */}
      <div className="mt-4">
        <h2 className="text-headline-md">Vaccine Certificate:</h2>
        <ul className="mt-3 list-disc space-y-3 pl-6">
          <li className="flex flex-wrap items-center gap-2">
            Vaccination(s):
            <Input
              className="w-64"
              value={value.vaccinations}
              onChange={(e) => onChange({ ...value, vaccinations: e.target.value })}
            />
          </li>
          <li className="flex flex-wrap items-center gap-2">
            Batch number(s):
            <Input
              className="w-64"
              value={value.batchNumbers}
              onChange={(e) => onChange({ ...value, batchNumbers: e.target.value })}
            />
          </li>
          <li className="flex flex-wrap items-center gap-2">
            Date(s) given:
            <Input
              className="w-64"
              value={value.datesGiven}
              onChange={(e) => onChange({ ...value, datesGiven: e.target.value })}
            />
          </li>
          <li className="flex flex-wrap items-center gap-2">
            Next Vaccine(s) Due:
            <Input
              className="w-64"
              value={value.nextVaccineDue}
              onChange={(e) => onChange({ ...value, nextVaccineDue: e.target.value })}
            />
          </li>
        </ul>
      </div>

      <p className="mt-6">
        Please feel free to contact us for any further information or clarification. On behalf of
        Snoots Vet.
      </p>

      <VetSignoffPreview
        vetName={value.vetName}
        onChange={(v) => onChange({ ...value, vetName: v.vetName })}
        label="Dr"
        showSignature
        showDate
      />
    </DocumentPreview>
  )
}

export const vaccinationCertificate = withDocumentEditor({
  id: 'vaccination-certificate',
  label: 'Certificate of Vaccination',
  defaultFields: defaultVaccinationCertificateFields,
  Preview: VaccinationCertificateForm,
  Pdf: VaccinationCertificatePdf,
})
